// Dates and hours in a time zone other than the device's (an IANA name, "Europe/Brussels"), with
// `Intl` only: the date and hour at a moment there, the moment of a local hour there, and dates as
// `YYYY-MM-DD` text. Moments are milliseconds since the epoch.

/** A moment's date and time in a time zone. */
export interface LocalParts {
    year: number;
    /** 1 to 12. */
    month: number;
    day: number;
    /** 0 to 23. */
    hour: number;
    minute: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

/** The date and time at `time` in the time zone `timeZone`. */
export function localParts(time: number, timeZone: string) : LocalParts {
    let formatter = formatters.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' });
        formatters.set(timeZone, formatter);
    }
    const parts = Object.fromEntries(formatter.formatToParts(time).map((part) => [part.type, part.value]));

    return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day), hour: Number(parts.hour) % 24, minute: Number(parts.minute) };
}

/** The moment it is `hour`:00 on a local date in `timeZone` (the date may overflow: day 32 is the next month's 1st). */
export function localTime(year: number, month: number, day: number, hour: number, timeZone: string) : number {
    const wanted = Date.UTC(year, month - 1, day, hour);
    let guess = wanted;
    // Twice: the offset at the guess may differ from the offset at the answer around a DST change.
    for (let step = 0; step < 2; step++) {
        const seen = localParts(guess, timeZone);
        guess += wanted - Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour);
    }

    return guess;
}

/** A date as text: `2026-10-05`. */
export const isoDate = (year: number, month: number, day: number) : string => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/** The date (`YYYY-MM-DD`) at `time` in `timeZone`. */
export function localDate(time: number, timeZone: string) : string {
    const { year, month, day } = localParts(time, timeZone);

    return isoDate(year, month, day);
}

/** The date after a date: `2026-10-31` → `2026-11-01`. */
export function dateAfter(date: string) : string {
    const next = new Date(`${date}T12:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);

    return next.toISOString().slice(0, 10);
}

/** The moment a local date (`2026-10-25`) begins in `timeZone`. */
export function localMidnight(date: string, timeZone: string) : number {
    const [year, month, day] = date.split('-').map(Number) as [number, number, number];

    return localTime(year, month, day, 0, timeZone);
}
